import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon, Video, ChevronLeft, ChevronRight, Plus, Link as LinkIcon } from "lucide-react";

// Media uploader: supports photo, video, URL, multiple images
export function MediaUploader({ value = [], onChange, maxFiles = 10, label = "Media" }) {
  const fileRef = useRef(null);
  const [urlInput, setUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const fd = new FormData();
    files.forEach(f => fd.append("files", f));
    try {
      const res = await axios.post(`${API}/upload/media`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      const newMedia = (res.data.media || []).map(m => ({
        url: `${API.replace('/api', '')}${m.url}`,
        media_type: m.media_type,
        media_id: m.media_id
      }));
      onChange([...value, ...newMedia].slice(0, maxFiles));
      toast.success(`${newMedia.length} file(s) uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const addUrl = () => {
    if (!urlInput.trim()) return;
    const isVideo = /\.(mp4|mov|avi|webm)(\?|$)/i.test(urlInput) || urlInput.includes("youtube") || urlInput.includes("vimeo");
    onChange([...value, { url: urlInput, media_type: isVideo ? "video" : "image", media_id: `url_${Date.now()}` }].slice(0, maxFiles));
    setUrlInput("");
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3" data-testid="media-uploader">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      
      {/* Current media */}
      {value.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {value.map((m, i) => (
            <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden bg-slate-100 border group">
              {m.media_type === "video" ? (
                <div className="w-full h-full flex items-center justify-center bg-slate-800"><Video className="w-6 h-6 text-white" /></div>
              ) : (
                <img src={m.url} alt="" className="w-full h-full object-cover" />
              )}
              <button onClick={() => remove(i)} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
      
      {/* Upload buttons */}
      <div className="flex flex-wrap gap-2">
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleFileUpload} />
        <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading || value.length >= maxFiles} className="text-xs">
          {uploading ? "Uploading..." : <><Upload className="w-3 h-3 mr-1" /> Upload from Device</>}
        </Button>
      </div>
      
      {/* URL input */}
      <div className="flex gap-2">
        <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="Paste image or video URL..." className="text-sm" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUrl())} />
        <Button type="button" size="sm" variant="outline" onClick={addUrl} disabled={!urlInput.trim()} className="shrink-0 text-xs"><LinkIcon className="w-3 h-3 mr-1" /> Add</Button>
      </div>
    </div>
  );
}

// Media display: shows single image, video, or auto-sliding carousel
export function MediaDisplay({ media = [], className = "aspect-video", autoSlide = true, interval = 4000 }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!autoSlide || media.length <= 1) return;
    const onlyImages = media.every(m => m.media_type === "image");
    if (!onlyImages) return;
    const timer = setInterval(() => setCurrent(p => (p + 1) % media.length), interval);
    return () => clearInterval(timer);
  }, [media.length, autoSlide, interval]);

  if (!media || media.length === 0) return null;

  // Single video
  if (media.length === 1 && media[0].media_type === "video") {
    return (
      <div className={`${className} bg-black rounded-lg overflow-hidden`}>
        <video src={media[0].url} controls playsInline className="w-full h-full object-contain" />
      </div>
    );
  }

  // Single image
  if (media.length === 1) {
    return (
      <div className={`${className} bg-slate-100 rounded-lg overflow-hidden`}>
        <img src={media[0].url} alt="" className="w-full h-full object-cover" />
      </div>
    );
  }

  // Multiple images — sliding carousel
  return (
    <div className={`${className} relative bg-slate-100 rounded-lg overflow-hidden group`} data-testid="media-carousel">
      {media.map((m, i) => (
        <div key={i} className={`absolute inset-0 transition-opacity duration-700 ${i === current ? 'opacity-100' : 'opacity-0'}`}>
          {m.media_type === "video" ? (
            <video src={m.url} controls={i === current} playsInline className="w-full h-full object-contain bg-black" />
          ) : (
            <img src={m.url} alt="" className="w-full h-full object-cover" />
          )}
        </div>
      ))}
      
      {/* Navigation arrows */}
      <button onClick={() => setCurrent(p => (p - 1 + media.length) % media.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"><ChevronLeft className="w-4 h-4" /></button>
      <button onClick={() => setCurrent(p => (p + 1) % media.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"><ChevronRight className="w-4 h-4" /></button>
      
      {/* Dots */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
        {media.map((_, i) => (
          <button key={i} onClick={() => setCurrent(i)} className={`w-2 h-2 rounded-full transition-colors ${i === current ? 'bg-white' : 'bg-white/40'}`} />
        ))}
      </div>
    </div>
  );
}
